import React, { useMemo, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { addToast } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from '@/hooks/useDebounce';
import CourseForm from '../../components/admin/courses/CourseForm';
import { useCourse, useCreateCourse, useUpdateCourse } from '@/hooks/queries/useCourses';
import { useInfiniteGuides } from '@/hooks/useGuides';
import { CourseFormData } from '../../components/admin/courses/schemas';
import { CreateCourseRequest, UpdateCourseRequest, GuideOrder } from '@/services/apis/courses';

interface CourseFormContainerProps {
  courseId?: string | null;
  onSuccess: () => void;
  onCancel?: () => void;
}

const CourseFormContainer: React.FC<CourseFormContainerProps> = ({ courseId, onSuccess, onCancel }) => {
  const { t } = useTranslation('admin');
  const [guideSearchQuery, setGuideSearchQuery] = useState('');
  const debouncedGuideSearch = useDebounce(guideSearchQuery, 500);

  const { data: course, isLoading: isLoadingCourse } = useCourse({
    id: courseId || '',
    shouldSkip: !courseId,
  });

  const createCourseMutation = useCreateCourse();
  const updateCourseMutation = useUpdateCourse();

  const defaultValues = useMemo<Partial<CourseFormData> | undefined>(() => {
    if (!course || isLoadingCourse) return undefined;

    const englishTranslation = course.translations?.find((t) => t.language === 'en');
    const spanishTranslation = course.translations?.find((t) => t.language === 'es');

    return {
      name: englishTranslation?.name || '',
      description: englishTranslation?.description || '',
      spanishName: spanishTranslation?.name || '',
      spanishDescription: spanishTranslation?.description || '',
      categoryIds: course.categories?.map((cat) => cat.id) || [],
      isRecommended: course.isRecommended || false,
      guides:
        course.courseGuides?.map((cg) => ({
          guideId: cg.guide.id,
          title: cg.guide.translations?.find((t) => t.language === 'en')?.title || '',
          isPublished: cg.guide.isPublished,
          position: cg.position,
        })) || [],
    };
  }, [course, isLoadingCourse]);

  // Fetch guides for autocomplete
  const { data: guidesData, isLoading: isLoadingGuides } = useInfiniteGuides({
    limit: 20,
    isPublished: true,
    search: debouncedGuideSearch || undefined,
  });

  const availableGuides = useMemo(() => {
    const allGuides =
      guidesData?.pages.flatMap((page) => {
        return page.data || [];
      }) || [];
    return allGuides;
  }, [guidesData]);

  const handleFormSubmit: SubmitHandler<CourseFormData> = async (data: CourseFormData) => {
    try {
      // Prepare guide orders from form data
      const guideOrders: GuideOrder[] = (data.guides || []).map((g) => ({
        guideId: g.guideId,
        position: g.position,
      }));

      if (courseId) {
        const updateData: UpdateCourseRequest = {
          name: data.name,
          description: data.description || undefined,
          isRecommended: data.isRecommended,
          categoryIds: data.categoryIds?.length ? data.categoryIds : undefined,
          spanishName: data.spanishName || undefined,
          spanishDescription: data.spanishDescription || undefined,
          guideOrders,
        };
        await updateCourseMutation.mutateAsync({ id: courseId, data: updateData });
        addToast({
          title: t('courses.actions.updateSuccess'),
          color: 'success',
        });
      } else {
        const createData: CreateCourseRequest = {
          name: data.name,
          description: data.description || undefined,
          categoryIds: data.categoryIds?.length ? data.categoryIds : undefined,
          guideOrders: guideOrders.length > 0 ? guideOrders : undefined,
          spanishName: data.spanishName || undefined,
          spanishDescription: data.spanishDescription || undefined,
          isRecommended: data.isRecommended,
        };
        await createCourseMutation.mutateAsync(createData);
        addToast({
          title: t('courses.actions.createSuccess'),
          color: 'success',
        });
      }
      onSuccess();
    } catch (error) {
      addToast({
        title: courseId ? t('courses.actions.updateError') : t('courses.actions.createError'),
        color: 'danger',
      });
    }
  };

  const isSubmitting = createCourseMutation.isPending || updateCourseMutation.isPending;

  return (
    <CourseForm
      defaultValues={defaultValues}
      courseId={courseId}
      onFormSubmit={handleFormSubmit}
      guideSearchQuery={guideSearchQuery}
      setGuideSearchQuery={setGuideSearchQuery}
      availableGuides={availableGuides}
      isLoadingGuides={isLoadingGuides}
      isSubmitting={isSubmitting}
      isLoadingCourse={isLoadingCourse}
      onCancel={onCancel}
    />
  );
};

export default CourseFormContainer;
